// src/hooks/usePageTitle.js
// Sets the page title, meta description and canonical URL on every route change.

import { useEffect } from 'react';

const DEFAULT_TITLE = "MAISON D'OUD | Premium Bold Scents";
const DEFAULT_DESCRIPTION = "Bold perfume oil fragrances designed to leave your mark. Each scent commands attention and lingers in memory — because you're not here to blend in.";
const BASE_URL = "https://www.maisondoud.com";

const DEFAULT_ROBOTS = 'index, follow';

const usePageTitle = (title, description, keywords, options = {}) => {
    // rawTitle: use `title` exactly as given (e.g. a Shopify SEO title)
    // instead of appending " — MAISON D'OUD".
    const { noindex = false, rawTitle = false } = options;

    useEffect(() => {
        // Set document title
        document.title = title
            ? (rawTitle ? title : `${title} — MAISON D'OUD`)
            : DEFAULT_TITLE;

        // Set meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', description || DEFAULT_DESCRIPTION);
        }

        // Meta keywords are ignored by Google and only reveal target keywords
        // to competitors, so we no longer set them (the `keywords` argument is
        // kept for compatibility but unused). Remove any leftover tag.
        document.querySelectorAll('meta[name="keywords"]').forEach(tag => tag.remove());

        // Set canonical URL dynamically per route
        // Prevents Google treating all pages as duplicates of the homepage
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', `${BASE_URL}${window.location.pathname}`);

        // Opt a page out of search indexing (e.g. the hidden /spin promo) —
        // overrides the global "index, follow" tag in index.html for as
        // long as this page is mounted.
        const metaRobots = document.querySelector('meta[name="robots"]');
        if (metaRobots && noindex) {
            metaRobots.setAttribute('content', 'noindex, nofollow');
        }

        // Reset to defaults on unmount
        return () => {
            document.title = DEFAULT_TITLE;
            if (metaDescription) {
                metaDescription.setAttribute('content', DEFAULT_DESCRIPTION);
            }
            if (metaRobots && noindex) {
                metaRobots.setAttribute('content', DEFAULT_ROBOTS);
            }
        };
    }, [title, description, keywords, noindex, rawTitle]);
};

export default usePageTitle;